<template>
    <CompassPanel :modelValue="showPanel" :title="panelTitle" @closed="closePanel">
        <template #default>
            <div v-if="effectiveTemplate && currentStep" class="automation-wizard-panel__content">
                <AutomationWizardOverviewContainer
                    v-if="currentStep.type === 'overview'"
                    :template="effectiveTemplate"
                    :isEditMode="isEditMode"
                />
                <template v-else>
                    <Heading
                        v-if="currentStep.title"
                        :labelTitle="currentStep.title"
                        variant="h3"
                        :prefixColoredIcon="stepColoredIcon"
                        class="automation-wizard-panel__heading"
                    />
                    <AutomationWizardForm
                        v-if="currentStep.fields?.length"
                        v-model="formData"
                        :currentStep="currentStep"
                        @field-change="clearFieldError"
                    />
                    <AutomationWizardCondition
                        v-if="currentStep.type === 'trigger' && conditionsEnabled"
                        ref="conditionRef"
                        v-model="formData.conditions"
                        :objectTypes="conditionsObjectTypes"
                        :propertyDefinitions="conditionsPropertyDefinitions"
                    />
                </template>
            </div>
        </template>
        <template #footer>
            <AutomationWizardPanelFooter
                :allSteps="allSteps"
                :currentStepIndex="currentStepIndex"
                :isLoading="isLoadingActivate"
                :isEditMode="isEditMode"
                :readOnly="readOnly"
                :template="effectiveTemplate"
                @activate-automation="handleActivateAutomation"
                @previous-step="goToPreviousStep"
                @next-step="handleNextStep"
            />
        </template>
    </CompassPanel>
</template>

<script>
import AutomationMixin from 'mixins/automation/AutomationMixin.js';
import AutomationWizardOverviewContainer from 'vcomponents/automation/templates/Wizard/Overview/AutomationWizardOverviewContainer.vue';
import AutomationWizardForm from 'vcomponents/automation/templates/Wizard/Form/AutomationWizardForm.vue';
import AutomationWizardPanelFooter from 'vcomponents/automation/templates/Wizard/AutomationWizardPanelFooter.vue';
import AutomationWizardCondition from 'vcomponents/automation/templates/Wizard/Condition/AutomationWizardCondition.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';

import {
    useAutomationTemplate,
    enableAutomation,
    updateAutomation,
    getAutomationTemplates,
} from 'api/automation/automationService.js';
import { AutomationEvent } from 'types/events';

export default {
    components: {
        AutomationWizardOverviewContainer,
        AutomationWizardForm,
        AutomationWizardPanelFooter,
        AutomationWizardCondition,
        Heading,
    },
    mixins: [AutomationMixin],
    props: {
        template: {
            type: Object,
            default: null,
        },
        automation: {
            type: Object,
            default: null,
        },
        isEditMode: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            translations: {
                title: this.$t.__('automation.AutomationWizardPanel.title'),
                titleEdit: this.$t.__('automation.AutomationWizardPanel.title_edit'),
            },
            isLoadingActivate: false,
            formData: {},
            currentStepIndex: 0,
            showPanel: false,
            loadedTemplate: null,
        };
    },
    computed: {
        allSteps() {
            return this.effectiveTemplate?.metadata?.wizard_schema || [];
        },
        currentStep() {
            return this.allSteps[this.currentStepIndex] || null;
        },
        panelTitle() {
            return this.isEditMode ? this.translations.titleEdit : this.translations.title;
        },
        stepColoredIcon() {
            const iconMap = {
                trigger: 'flash',
                action: 'target',
            };
            const icon = iconMap[this.currentStep?.type] || '';
            const color = this.currentStep?.type === 'trigger' ? 'teal' : 'coral';
            return { icon, color };
        },
        effectiveTemplate() {
            if (this.isEditMode && this.automation?.metadata) {
                return {
                    id: this.automation.id,
                    metadata: this.automation.metadata,
                    title:
                        this.automation.version?.displayName ||
                        this.automation.metadata?.template.title,
                    description: this.automation.metadata?.template?.description || '',
                    pieces: this.extractPiecesFromAutomation(),
                };
            }
            return this.loadedTemplate || this.template;
        },
        isOpenedFromExternalContext() {
            return !!this.template?.internal_id;
        },
        conditionsEnabled() {
            const wc = this.effectiveTemplate?.metadata?.wizard_conditions;
            return !!(wc?.conditions?.length || wc?.customfieldsAvailableObjects?.length);
        },
        conditionsObjectTypes() {
            const type = this.effectiveTemplate?.metadata?.wizard_conditions?.customfieldsAvailableObjects;
            if (!type) {
                return ['document', 'opportunity', 'contact', 'client', 'prospect', 'supplier'];
            }
            return Array.isArray(type) ? type : [type];
        },
        conditionsPropertyDefinitions() {
            const conditions = this.effectiveTemplate?.metadata?.wizard_conditions?.conditions || [];
            const uniqueById = new Map();
            for (const condition of conditions) {
                if (condition.scope === 'object.properties' && !uniqueById.has(condition.id)) {
                    uniqueById.set(condition.id, condition);
                }
            }
            return Array.from(uniqueById.values());
        },
    },
    watch: {
        $route() {
            this.closePanel();
        },
        template() {
            this.resetPanel();
        },
        automation() {
            this.resetPanel();
        },
        showPanel(newValue) {
            if (newValue) {
                this.resetPanel();
            }
        },
    },
    created() {
        if (this.isOpenedFromExternalContext) {
            this.loadTemplateByInternalId();
        } else {
            this.initializeFormData();
        }
    },
    mounted() {
        this.showPanel = true;
        document.addEventListener('keydown', this.handleEscapeKey);
    },
    unmounted() {
        document.removeEventListener('keydown', this.handleEscapeKey);
    },
    methods: {
        handleEscapeKey(event) {
            if (event.key === 'Escape' && this.showPanel) {
                this.closePanel();
            }
        },
        resetPanel() {
            this.currentStepIndex = 0;
            this.clearAllErrors();
            this.initializeFormData();
        },
        closePanel() {
            this.clearAllErrors();
            this.showPanel = false;
            this.$nextTick(() => this.$emit('close'));
        },
        activateAutomation(id) {
            return enableAutomation(id)
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__('automation.common.success.start_automation'),
                    });
                })
                .catch((error) => {
                    let textToast = this.$t.__('automation.common.error.stop_automation');
                    if (error.response.status === 409) {
                        textToast = this.$t.__(
                            'automation.automationListingTableRow.toast.error.wait_minute',
                        );
                    }
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: textToast,
                    });
                    throw error;
                });
        },
        async handleActivateAutomation() {
            this.$refs.conditionRef?.removeUnavailableConditions();
            this.isLoadingActivate = true;
            try {
                if (this.isEditMode) {
                    await this.updateExistingAutomation();
                    this.navigateToListing();
                } else {
                    await this.createNewAutomation();
                    if (this.isOpenedFromExternalContext) {
                        this.closePanel();
                    } else {
                        this.navigateToListing();
                    }
                }
            } catch (error) {
                if (this.isEditMode) {
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('common.labels.toast.error'),
                    });
                }
                throw error;
            } finally {
                this.isLoadingActivate = false;
            }
        },
        async createNewAutomation() {
            const preparedData = this.prepareFormDataForSubmission(
                this.effectiveTemplate,
                this.template,
                this.formData,
            );

            let result;
            try {
                result = await useAutomationTemplate(
                    this.effectiveTemplate.id,
                    preparedData,
                );
            } catch (error) {
                this.$pushToast({
                    type: 'error',
                    close: true,
                    text: this.$t.__(
                        'automation.AutomationLayout.error.create_automation',
                    ),
                });
                throw error;
            }

            await this.activateAutomation(result.data.id);
        },
        async updateExistingAutomation() {
            const preparedData = this.prepareFormDataForSubmission(
                this.effectiveTemplate,
                this.template,
                this.formData,
            );
            await updateAutomation(this.automation.id, preparedData);
            this.$pushToast({
                type: 'success',
                close: true,
                text: this.$t.__('automation.common.success.automation_updated'),
            });
        },
        navigateToListing() {
            this.closePanel();
            if (this.isEditMode) {
                EventBus.emit(AutomationEvent.UPDATED);
            } else {
                this.$router.push({ name: 'automations.listing' });
            }
        },
        goToPreviousStep() {
            if (this.currentStepIndex > 0) {
                this.currentStepIndex--;
            }
        },
        goToNextStep() {
            if (this.currentStepIndex < this.allSteps.length - 1) {
                this.currentStepIndex++;
            }
        },
        handleNextStep() {
            if (this.isLoadingActivate) {
                return;
            }
            this.isLoadingActivate = true;
            const isLastStep = this.currentStepIndex === this.allSteps.length - 1;
            if (isLastStep) {
                if (this.validateCurrentStep()) {
                    this.handleActivateAutomation();
                } else {
                    this.isLoadingActivate = false;
                }
            } else if (this.validateCurrentStep()) {
                this.isLoadingActivate = false;
                this.goToNextStep();
            } else {
                this.isLoadingActivate = false;
            }
        },
        validateCurrentStep() {
            if (!this.currentStep) {
                return true;
            }
            let hasErrors = false;
            if (this.currentStep.fields) {
                for (const field of this.currentStep.fields) {
                    delete field.currentError;
                }
                const requiredFields = this.currentStep.fields.filter(
                    (field) => field.required && field.type !== 'hidden',
                );
                for (const field of requiredFields) {
                    const fieldValue = this.formData[field.id];
                    const isEmpty = this.isFieldValueEmpty(fieldValue);

                    if (isEmpty) {
                        field.currentError = field.props?.formItem?.error;
                        hasErrors = true;
                    }
                }
                hasErrors |= !this.validateNumericFields();
            }

            return !hasErrors;
        },
        validateNumericFields() {
            const numericFields = this.currentStep.fields.filter(
                (field) => field.type === 'percent' || field.type === 'amount',
            );

            for (const field of numericFields) {
                const fieldValue = this.formData[field.id];
                if (fieldValue !== '' && fieldValue !== null && fieldValue !== undefined) {
                    const numValue = Number(fieldValue);
                    // Min default=0
                    const minValue = field.props?.formItem?.min ?? 0;
                    if (!isNaN(numValue) && minValue > numValue) {
                        field.currentError = this.$t.__('common.errors.min_value', {
                            min: minValue,
                        });
                        return false;
                    }
                    if (
                        field.props?.formItem?.max !== undefined &&
                        !isNaN(numValue) &&
                        numValue > field.props.formItem.max
                    ) {
                        field.currentError = this.$t.__('common.errors.max_value', {
                            max: field.props.formItem.max,
                        });
                        return false;
                    }
                }
            }

            return true;
        },
        isFieldValueEmpty(value) {
            return !value || (Array.isArray(value) && value.length === 0);
        },
        clearFieldError(fieldName) {
            if (!this.currentStep || !this.currentStep.fields) {
                return;
            }
            const field = this.currentStep.fields.find((f) => f.id === fieldName);
            if (field && field.currentError) {
                delete field.currentError;
            }
        },
        clearAllErrors() {
            if (!this.effectiveTemplate?.metadata?.wizard_schema) {
                return;
            }
            for (const step of this.effectiveTemplate.metadata.wizard_schema) {
                if (step.fields) {
                    for (const field of step.fields) {
                        if (field.currentError) {
                            delete field.currentError;
                        }
                    }
                }
            }
        },
        extractPiecesFromAutomation() {
            if (this.automation?.pieces && Array.isArray(this.automation.pieces)) {
                return this.automation.pieces;
            }
            const pieces = new Set();
            if (this.automation?.version?.trigger?.settings?.pieceName) {
                pieces.add(this.automation.version.trigger.settings.pieceName);
            }
            if (this.automation?.metadata?.wizard_schema) {
                for (const step of this.automation.metadata.wizard_schema) {
                    if (step.stepName && step.stepName.includes('@activepieces/piece-')) {
                        const match = step.stepName.match(/@activepieces\/piece-[^/]+/);
                        if (match) {
                            pieces.add(match[0]);
                        }
                    }
                }
            }
            return Array.from(pieces);
        },
        async loadTemplateByInternalId() {
            const internalId = this.template?.internal_id;
            if (!internalId) {
                return;
            }
            try {
                const response = await getAutomationTemplates();
                const templates = response.data.data;
                const matchingTemplate = templates.find(
                    (template) => template.metadata?.template?.internal_id === internalId,
                );
                if (matchingTemplate) {
                    this.loadedTemplate = {
                        id: matchingTemplate.id,
                        title: matchingTemplate.name,
                        description: matchingTemplate.description,
                        version: matchingTemplate.template,
                        metadata: matchingTemplate.metadata,
                        tags: matchingTemplate.tags || [],
                    };
                    this.initializeFormData();
                }
            } catch (error) {
                this.$pushToast({
                    type: 'error',
                    close: true,
                    text: this.$t.__('common.labels.toast.error'),
                });
                throw error;
            }
        },
        initializeFormData() {
            if (!this.effectiveTemplate?.metadata?.wizard_schema) {
                this.formData = {};
                return;
            }
            const newFormData = {};
            for (const step of this.effectiveTemplate.metadata.wizard_schema) {
                if (step.fields) {
                    for (const field of step.fields) {
                        let initialValue = '';
                        if (field.isMappedField && field.value !== undefined) {
                            initialValue = field.value;
                        } else if (field.props?.select?.value !== undefined) {
                            initialValue = field.props.select.value;
                        } else if (field.props?.iconButton?.value !== undefined) {
                            initialValue = field.props.iconButton.value;
                        } else if (field.props?.input?.value !== undefined) {
                            initialValue = field.props.input.value;
                        }
                        newFormData[field.id] = initialValue;
                    }
                }
            }

            newFormData.conditions = (
                this.effectiveTemplate?.metadata?.wizard_conditions?.conditions || []
            ).map((condition) => ({ ...condition }));

            this.formData = newFormData;
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-wizard-panel {
    &__content > &__heading {
        margin: 0 0 var(--spacing-4);
    }

    &__content {
        display: flex;
        flex-direction: column;
    }
}
</style>
<style lang="scss">
/* Ensures dropdown menus render above the CompassPanel overlay (z-index: 1000) */
#compass-dropdown-container {
    .select-automation-wizard {
        z-index: 1001;
    }
}
</style>
